import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, Clock, ExternalLink, RefreshCw, QrCode, Download } from 'lucide-react';
import { Entry, Game } from '../types';
import { getEntry, createPaymentRequest, verifyPayment, fetchGames } from '../services/api';

interface PaymentPayload {
  payloadId: string;
  uuid: string;
  qrCode?: string;
  deepLink?: string;
  refs?: {
    qr_png?: string;
  };
  next?: any;
}

const Status = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const [entry, setEntry] = useState<Entry | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentPayload, setPaymentPayload] = useState<PaymentPayload | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (entryId) {
      loadEntry();
    }
  }, [entryId]);

  const loadEntry = async () => {
    if (!entryId) return;
    
    try {
      setLoading(true);
      const entryData = await getEntry(entryId);
      setEntry(entryData);
      
      // Load games for the entry's date
      if (entryData.date) {
        const gamesData = await fetchGames(entryData.date);
        setGames(gamesData.games);
      }
    } catch (error) {
      toast.error('Failed to load entry');
      console.error('Error loading entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTeamNameForPick = (gameId: string, pick: 'home' | 'away'): string => {
    const game = games.find(g => g.id === gameId);
    if (!game) return `${pick} team`;
    return pick === 'home' ? game.homeTeam : game.awayTeam;
  };

  const downloadMyPicks = () => {
    if (!entry) return;

    // Create a clean text format for the user
    let content = `NBA PICK'EM ENTRY - ${entry.date}\n`;
    content += `=======================================\n\n`;
    
    content += `ENTRY INFORMATION:\n`;
    content += `Entry ID: ${entry.id}\n`;
    content += `X Handle: ${entry.xHandle}\n`;
    content += `Date: ${entry.date}\n`;
    content += `Tie-breaker Prediction: ${entry.tieBreakTotalScore} points\n`;
    content += `Payment Status: ${entry.paid ? 'PAID' : 'PENDING'}\n`;
    content += `Entry Created: ${new Date(entry.createdAt).toLocaleString()}\n\n`;
    
    content += `MY PICKS:\n`;
    content += `=========\n`;
    
    if (entry.picks && entry.picks.length > 0) {
      entry.picks.forEach((pick, index) => {
        const teamName = getTeamNameForPick(pick.gameId, pick.pick);
        const game = games.find(g => g.id === pick.gameId);
        const matchup = game ? `${game.awayTeam} @ ${game.homeTeam}` : 'Game info loading...';
        
        content += `${index + 1}. ${matchup}\n`;
        content += `   → PICKED: ${teamName}\n\n`;
      });
    } else {
      content += `No picks recorded\n`;
    }
    
    content += `\n=======================================\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `NBA Pick'em Contest - Powered by $NUTS\n`;

    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `my-nba-picks-${entry.date}-${entry.xHandle.replace('@', '')}.txt`;
    link.click();
    
    toast.success('Your picks have been downloaded!');
  };

  const handlePayment = async () => {
    console.log('🎯 PAYMENT BUTTON CLICKED!');
    console.log('Entry:', entry);
    console.log('Entry ID:', entry?.id);
    console.log('Entry paid status:', entry?.paid);
    
    if (!entry || entry.paid) {
      console.log('❌ Payment blocked - no entry or already paid');
      return;
    }
    
    try {
      console.log('🚀 Starting payment process...');
      setPaymentLoading(true);
      toast.loading('Creating payment request...');
      
      console.log('📡 Calling createPaymentRequest with entry ID:', entry.id);
      const payload = await createPaymentRequest(entry.id);
      console.log('✅ Payment payload received:', payload);
      
      // Validate payload structure - using MLB format
      if (!payload || !payload.uuid) {
        throw new Error('Invalid payment payload received');
      }
      
      setPaymentPayload(payload);
      
      toast.dismiss();
      toast.success('Payment QR code generated! Scan with Xaman app.');
      
      // Start polling for payment status using payloadId (same as uuid)
      if (payload.payloadId || payload.uuid) {
        pollPaymentStatus(payload.payloadId || payload.uuid);
      } else {
        throw new Error('No payment ID received');
      }
      
    } catch (error: any) {
      console.error('Payment creation error:', error);
      toast.dismiss();
      toast.error(error.message || 'Failed to create payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const pollPaymentStatus = (paymentId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await verifyPayment(paymentId);
        // console.log('Payment verify status:', status);
        if (status.state === 'completed' && status.txHash) {
          clearInterval(interval);
            toast.success('Payment successful!');
            const updatedEntry = {
              ...entry!,
              paid: true,
              paidTxHash: status.txHash,
              paidAt: new Date().toISOString()
            } as any;
            setEntry(updatedEntry);
            setPaymentPayload(null);
            // Don't reload from database - keep the local paid state
            // The database will be updated by the backend automatically
        } else if (status.state === 'cancelled') {
          clearInterval(interval);
          toast.error('Payment cancelled');
          setPaymentPayload(null);
        } else if (status.state === 'expired') {
          clearInterval(interval);
          toast.error('Payment expired');
          setPaymentPayload(null);
        }
        // pending: keep QR visible
      } catch (e) {
        console.error('Payment poll error', e);
        clearInterval(interval);
        toast.error('Payment polling error');
      }
    }, 5000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Entry not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Entry Status</h1>
        <p className="mt-2 text-lg text-gray-600">
          NBA Pick'em Contest - {entry.date || 'TBD'}
        </p>
      </div>

      {/* Payment Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Payment Status</h2>
          {entry.paid ? (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span className="font-medium">Paid</span>
            </div>
          ) : (
            <div className="flex items-center text-orange-600">
              <Clock className="h-5 w-5 mr-2" />
              <span className="font-medium">Pending Payment</span>
            </div>
          )}
        </div>

        {!entry.paid && !paymentPayload && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-orange-400 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-orange-800">Payment Required</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    Complete your payment of 1000 $NUTS to finalize your entry.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handlePayment}
              disabled={paymentLoading}
              className="btn btn-primary w-full"
            >
              {paymentLoading ? (
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating Payment...
                </div>
              ) : (
                <>
                  <QrCode className="h-4 w-4 mr-2" />
                  <span>Pay with Xaman</span>
                </>
              )}
            </button>
          </div>
        )}

        {!entry.paid && paymentPayload && (
          <div className="space-y-4">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-orange-800 mb-4">
                <QrCode className="h-5 w-5 inline mr-2" />
                Scan QR Code with Xaman
              </h4>
              {(paymentPayload.refs?.qr_png || paymentPayload.qrCode) ? (
                <div className="bg-white p-4 rounded-lg border shadow-sm inline-block">
                  <img 
                    src={paymentPayload.refs?.qr_png || paymentPayload.qrCode} 
                    alt="Payment QR Code"
                    className="w-48 h-48"
                  />
                </div>
              ) : (
                <div className="bg-white p-4 rounded-lg border shadow-sm inline-block">
                  <div className="w-48 h-48 flex items-center justify-center bg-gray-100 text-gray-500">
                    QR Code Loading...
                  </div>
                </div>
              )}
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-sm text-orange-700">
                Scan this QR code with your Xaman app to pay 1000 $NUTS
              </p>
              <p className="text-xs text-orange-600">
                Payment will be processed automatically. Do not close this page.
              </p>
              
              <div className="flex justify-center space-x-4 mt-4">
                {paymentPayload.next?.always && (
                  <a
                    href={paymentPayload.next.always}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open Xaman
                  </a>
                )}
                
                <button
                  onClick={() => setPaymentPayload(null)}
                  className="btn btn-outline text-sm"
                >
                  Cancel Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {entry.paid && entry.paidTxHash && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Payment Confirmed</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your entry is now active in the contest.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              <span className="font-medium">Transaction Hash:</span>{' '}
              <span className="font-mono text-xs break-all">{entry.paidTxHash}</span>
            </div>
          </div>
        )}
      </div>

      {/* Entry Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Entry Details</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Player Information</h3>
            <div className="space-y-2 text-sm">
              <div><span className="font-medium">X Handle:</span> {entry.xHandle}</div>
              <div><span className="font-medium">XRPL Address:</span> 
                <span className="font-mono text-xs block mt-1">{entry.xrplAddress}</span>
              </div>
              <div><span className="font-medium">Date:</span> {entry.date}</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Tie-breaker Prediction</h3>
            <div className="text-2xl font-bold text-indigo-600">
              {entry.tieBreakTotalScore} points
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total combined score of final game
            </p>
          </div>
        </div>
      </div>

      {/* Picks */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Your Picks</h2>
          {entry.picks.length > 0 && (
            <button
              onClick={downloadMyPicks}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Download My Picks
            </button>
          )}
        </div>
        
        {entry.picks.length > 0 ? (
          <div className="space-y-3">
            {entry.picks.map((pick, index) => (
              <div key={pick.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium">Game {index + 1}</span>
                </div>
                <div className="text-sm font-medium">
                  Pick: <span className="text-indigo-600">{getTeamNameForPick(pick.gameId, pick.pick)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No picks recorded</p>
        )}
      </div>

      {/* Next Steps */}
      {entry.paid && (
        <div className="text-center">
          <a
            href="/leaderboard"
            className="btn btn-primary"
          >
            View Leaderboard
          </a>
        </div>
      )}
    </div>
  );
};

export default Status;
